import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Snackbar } from '@mui/material';
import { FormatListBulleted, SaveAlt, EventBusyOutlined } from '@mui/icons-material';
import comms from '../comms';
import { classifyTripStatus } from '../../utils/tripStatusClassification';
import {
  sendPullTrip,
  initializeItinerarySocket,
} from '../Dispatch/Itinerary/itinerarySocketHelper';

// Mixed-dispatch trip marketplace — browse unassigned trips and either claim one
// outright or bid for dispatcher approval. The server decides whether each result is
// routed or live-dispatch work and which actions are allowed (canClaim / canBid); this
// screen only displays that classification, it never infers it.
//
// Trips inside LIVE_DISPATCH_WINDOW_MINUTES of pickup are deliberately excluded from the
// browsable pool: those reach the driver through the live-dispatch job-offer flow
// (JOBOM → ACPTM) instead.
const LIVE_DISPATCH_WINDOW_MINUTES = 30;

// Top-level scopes, matching the rest of the app's Bookings-style tabs.
const SCOPES = [
  { id: 'available', label: 'Available', Icon: FormatListBulleted },
  { id: 'completed', label: 'Completed', Icon: SaveAlt },
];

/** localStorage can hold truncated/corrupt JSON; never let that throw during render. */
function readJson(key, fallback = null) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    return fallback;
  }
}

// YYYY-MM-DD in the handset's local (fleet-local) date, never a UTC-derived date.
function toServiceDate(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function todayLabel() {
  const d = new Date();
  return `Today ${String(d.getDate()).padStart(2, '0')} ${d.toLocaleDateString([], { month: 'short' })}`;
}

// Scheduled pickup as a Date, from scheduledEpoch (seconds) when present, else
// pickupDateTime. Returns null when neither is usable.
function pickupDate(trip) {
  const epoch = Number(trip?.scheduledEpoch);
  if (Number.isFinite(epoch) && epoch > 0) return new Date(epoch * 1000);
  const parsed = new Date(trip?.pickupDateTime ?? '');
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// Minutes from now until scheduled pickup; null when the trip carries no usable time,
// which the pool filter treats as "don't hide it" rather than guessing.
export function minutesUntilPickup(trip, now = Date.now()) {
  const d = pickupDate(trip);
  return d ? (d.getTime() - now) / 60000 : null;
}

// "How long until pickup", for the card chip. Null when the trip has no usable time.
function timeUntilLabel(trip, now) {
  const mins = minutesUntilPickup(trip, now);
  if (mins === null) return null;
  if (mins < 0) return 'now';
  const total = Math.round(mins);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? `in ${h}h ${m}m` : `in ${m}m`;
}

// Scheduled trip duration in minutes, when both ends are known.
function tripDurationMinutes(trip) {
  const start = pickupDate(trip);
  const end = trip?.dropoffDateTime ? new Date(trip.dropoffDateTime) : null;
  if (!start || !end || Number.isNaN(end.getTime())) return null;
  const mins = (end.getTime() - start.getTime()) / 60000;
  return mins > 0 ? Math.round(mins) : null;
}

/** Join non-empty parts with a separator, skipping blanks. */
function joinParts(parts, sep = ' ') {
  return parts.map((v) => (v == null ? '' : String(v).trim())).filter(Boolean).join(sep);
}

/**
 * Best available street address, given the several shapes these endpoints use:
 * a prebuilt single string, split AddressLine1/2 (number + street), or a landmark.
 */
function composeAddress(t, prebuiltKeys, line1Key, line2Key, cityKey, stateKey, zipKey) {
  for (const k of prebuiltKeys) {
    const v = t?.[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  const street = joinParts([t?.[line1Key], t?.[line2Key]]);
  const locality = joinParts([t?.[cityKey], joinParts([t?.[stateKey], t?.[zipKey]])], ', ');
  return joinParts([street, locality], ', ');
}

// Normalize a Schedule row (a trip already assigned to this driver) into card fields.
function normalizeAssigned(t, i) {
  return {
    tripId: t.Id || t.id || t.TripId || `A-${i}`,
    tripNo: t.TripNo ?? t.tripNo ?? '',
    passengerName: t.PassengerName || t.passengerName || '',
    pickup: composeAddress(
      t,
      ['startAddress', 'Origin', 'origin', 'OriginAddress', 'pickup'],
      'Origin_AddressLine1', 'Origin_AddressLine2',
      'Origin_City', 'Origin_State', 'Origin_Zip'
    ),
    dropoff: composeAddress(
      t,
      ['endAddress', 'Destination', 'destination', 'DestinationAddress', 'dropoff'],
      'Destination_AddressLine1', 'Destination_AddressLine2',
      'Destination_City', 'Destination_State', 'Destination_Zip'
    ),
    pickupDateTime: t.OriginScheduledDate || t.OriginScheduledDateTime || '',
    dropoffDateTime: t.DestinationScheduledDate || t.DestinationScheduledDateTime || '',
    tripStatus: t.TripStatus || '',
    assignedToMe: true,
  };
}

// Normalize a History row into the fields this screen's card renders.
function normalizeHistory(t, i) {
  return {
    tripId: t.Id || t.id || `H-${i}`,
    tripNo: t.TripNo || t.tripNo || '',
    passengerName: t.PassengerName || t.passengerName || '',
    pickup: composeAddress(
      t,
      ['Origin', 'origin', 'startAddress', 'OriginAddress', 'pickup'],
      'Origin_AddressLine1', 'Origin_AddressLine2',
      'Origin_City', 'Origin_State', 'Origin_Zip'
    ),
    dropoff: composeAddress(
      t,
      ['Destination', 'destination', 'endAddress', 'DestinationAddress', 'dropoff'],
      'Destination_AddressLine1', 'Destination_AddressLine2',
      'Destination_City', 'Destination_State', 'Destination_Zip'
    ),
    pickupDateTime: t.OriginScheduledDateTime || t.OriginScheduledDate || t.date || '',
    dropoffDateTime: t.DestinationScheduledDateTime || t.DestinationScheduledDate || '',
    tripStatus: t.TripStatus || 'Completed',
  };
}

function Marketplace({ userSettings } = {}) {
  const [scope, setScope] = useState('available'); // available | completed
  const [subTab, setSubTab] = useState('trips'); // trips | active
  const [trips, setTrips] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [offers, setOffers] = useState([]); // this driver's pending bids
  const [assigned, setAssigned] = useState([]); // trips already assigned to this driver
  const [status, setStatus] = useState('loading'); // loading | loaded | error
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [busyTripId, setBusyTripId] = useState(null);
  const [toast, setToast] = useState(null);
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );

  const driverId = userSettings?.Id || readJson('userSettings', {})?.Id;
  const selectedVehicle = readJson('selectedVehicle');
  const vehicleId = selectedVehicle?.Id || selectedVehicle?.id || null;

  // The marketplace always browses today; the server's own horizon governs what it
  // returns. (A date picker was removed deliberately — see the screen spec.)
  const serviceDate = useMemo(() => toServiceDate(new Date()), []);

  // Drives the "in Xm" countdown chips. One timer for the whole list; a minute is fine
  // granularity for a pickup that is at least 30 minutes out.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // Claim is confirmed over the itinerary socket's `itinerary_ack`, but that listener is
  // normally attached by the Itinerary/DemandResponse screens — neither of which is mounted
  // when the marketplace is opened from the Dashboard. Without it a claim's reply has
  // nowhere to land and the send times out even though the server accepted it. Attaching is
  // idempotent (a no-op when a dispatch screen already owns the socket), and no route-update
  // callback is passed because this screen renders no itinerary.
  useEffect(() => {
    const socket = comms.getSocket?.();
    if (!socket) return undefined;
    const cleanup = initializeItinerarySocket(socket);
    return () => {
      if (typeof cleanup === 'function') cleanup();
    };
  }, []);

  // Claim and bid are both live, server-confirmed actions — never queue them offline.
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  const load = useCallback(async () => {
    if (!driverId) {
      setError('Driver not identified.');
      setStatus('error');
      return;
    }
    setStatus('loading');
    setError(null);
    try {
      if (scope === 'completed') {
        const res = await comms.getDriverHistory(driverId);
        const raw = res?.trips || res?.history || res?.result || res?.data || [];
        setCompleted((Array.isArray(raw) ? raw : []).map(normalizeHistory));
        setStatus('loaded');
        return;
      }

      const params = { serviceDate, driverId };
      if (vehicleId) params.vehicleId = vehicleId;

      const [tripRes, offerRes, schedRes] = await Promise.all([
        comms.getAvailableTrips(params),
        // Recovers pending bid ids so a bid stays withdrawable across a reload.
        comms.getDriverOffers(vehicleId ? { driverId, vehicleId } : { driverId }).catch(() => null),
        // A claim creates no TripOffer, so a claimed trip is only visible as ordinary
        // assigned work — this is what makes it show up under Active.
        comms.getDriverSchedule(driverId, serviceDate).catch(() => null),
      ]);

      if (tripRes && tripRes.available === false) {
        setUnavailable(true);
        setTrips([]);
        setOffers([]);
        setStatus('loaded');
        return;
      }

      setUnavailable(false);
      setTrips(Array.isArray(tripRes?.trips) ? tripRes.trips : []);
      setOffers(Array.isArray(offerRes?.offers) ? offerRes.offers : []);

      const schedTrips = Array.isArray(schedRes?.trips)
        ? schedRes.trips
        : Array.isArray(schedRes?.schedule?.trips)
        ? schedRes.schedule.trips
        : [];
      setAssigned(
        schedTrips
          .map(normalizeAssigned)
          .filter((t) => classifyTripStatus(t.tripStatus) !== 'completed')
      );
      setStatus('loaded');
    } catch (e) {
      setError('Could not load trips. Please try again.');
      setStatus('error');
    }
  }, [driverId, vehicleId, serviceDate, scope]);

  useEffect(() => {
    load();
  }, [load]);

  // Refresh when returning to the screen — a claim elsewhere may have taken a row.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', load);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', load);
    };
  }, [load]);

  const offerForTrip = useCallback(
    (tripId) =>
      offers.find(
        (o) => String(o.TripId ?? o.tripId ?? '').toLowerCase() === String(tripId).toLowerCase()
      ) || null,
    [offers]
  );

  // The browsable pool excludes anything already inside the live-dispatch window, and
  // anything the driver has a pending bid on (that lives in the Active tab).
  const availableTrips = useMemo(
    () =>
      trips.filter((t) => {
        if (offerForTrip(t.tripId)) return false;
        const mins = minutesUntilPickup(t);
        return mins === null || mins >= LIVE_DISPATCH_WINDOW_MINUTES;
      }),
    [trips, offerForTrip]
  );

  // "Active" = this driver's pending bids. Built from offers[] rather than the trip pool,
  // so it still lists a bid whose trip the server has already removed from the pool, and
  // regardless of how close its pickup is.
  // "Active" is the driver's own marketplace work: trips already assigned to them (a
  // successful Claim, or an approved bid — neither leaves a TripOffer behind) plus bids
  // still awaiting a decision. Built from the schedule and offers rather than the
  // unassigned pool, which by definition no longer contains any of them.
  const activeTrips = useMemo(() => {
    const byId = new Map(trips.map((t) => [String(t.tripId).toLowerCase(), t]));
    const seen = new Set();
    const out = [];

    assigned.forEach((t) => {
      const key = String(t.tripId).toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push(t);
    });

    offers.forEach((o) => {
      const tripId = String(o.TripId ?? o.tripId ?? '');
      const key = tripId.toLowerCase();
      if (!tripId || seen.has(key)) return;
      seen.add(key);
      const known = byId.get(key);
      out.push(
        known || {
          tripId,
          tripNo: o.TripNo ?? o.tripNo ?? null,
          pickupDateTime: o.PickupDateTime ?? o.pickupDateTime,
        }
      );
    });

    return out;
  }, [offers, trips, assigned]);

  const guard = () => {
    if (!online) {
      setToast('Connect to claim or bid on a trip.');
      return false;
    }
    if (!driverId) {
      setToast('Driver not identified.');
      return false;
    }
    return true;
  };

  const handleClaim = async (trip) => {
    if (!guard()) return;
    setBusyTripId(trip.tripId);
    try {
      // Only the trip's OWN route is a legitimate insertion target. The driver's active
      // route is not (it is a different route), and a live-dispatch trip has none at all,
      // so anything else would ask the server to insert into a route that cannot hold it.
      const routeId = trip.routeId || undefined;

      // Prefer REST: it lets the server classify routed vs live and can commit a
      // route-less live claim. The pullTrip socket action runs VROOM against a specific
      // route, so it only ever works for a routed trip.
      const res = await comms.claimTrip({ tripId: trip.tripId, driverId, vehicleId, routeId });

      if (res?.status === true) {
        setToast('Trip claimed.');
      } else if (res?.available === false) {
        // No REST endpoint on this server — fall back to the socket action, which needs
        // a route and therefore cannot serve a live-dispatch trip.
        if (!routeId) {
          setToast('This trip has to be assigned by dispatch.');
        } else {
          await sendPullTrip({ tripId: trip.tripId, driverId, vehicleId, serviceDate, routeId });
          setToast('Trip claimed.');
        }
      } else {
        setToast(res?.error || res?.message || "Couldn't claim trip, try again.");
      }
    } catch (e) {
      setToast(e?.message || "Couldn't claim trip, try again.");
    } finally {
      setBusyTripId(null);
      // The server is authoritative for both outcomes — never drop the row locally.
      load();
    }
  };

  const handleBid = async (trip) => {
    if (!guard()) return;
    setBusyTripId(trip.tripId);
    try {
      const routeId = trip.routeId || undefined;
      const res = await comms.submitTripBid({ tripId: trip.tripId, driverId, vehicleId, routeId });
      if (res?.status === true) {
        // An auto-approved bid is already an assignment, not a pending decision.
        setToast(res.autoApproved ? 'Bid approved — trip assigned.' : 'Bid submitted for approval.');
      } else {
        setToast(res?.error || res?.message || "Couldn't submit bid, try again.");
      }
    } catch (e) {
      setToast(e?.message || "Couldn't submit bid, try again.");
    } finally {
      setBusyTripId(null);
      load();
    }
  };

  const handleWithdraw = async (trip) => {
    if (!guard()) return;
    const offer = offerForTrip(trip.tripId);
    const offerId = offer?.Id || offer?.offerId;
    if (!offerId) {
      setToast('That bid is no longer pending.');
      load();
      return;
    }
    setBusyTripId(trip.tripId);
    try {
      const res = await comms.respondTripOffer({ offerId, action: 'withdraw', driverId, vehicleId });
      setToast(
        res?.status === true
          ? 'Bid withdrawn.'
          : res?.error || res?.message || "Couldn't withdraw bid, try again."
      );
    } catch (e) {
      setToast(e?.message || "Couldn't withdraw bid, try again.");
    } finally {
      setBusyTripId(null);
      load();
    }
  };

  const renderCard = (trip) => {
    const isCompleted = scope === 'completed';
    const pendingBid = offerForTrip(trip.tripId);
    const busy = busyTripId === trip.tripId;
    const duration = tripDurationMinutes(trip);
    const untilPickup = isCompleted ? null : timeUntilLabel(trip, now);
    const pickupText = composeAddress(
      trip,
      ['pickup', 'Origin', 'origin', 'startAddress', 'OriginAddress'],
      'Origin_AddressLine1', 'Origin_AddressLine2',
      'Origin_City', 'Origin_State', 'Origin_Zip'
    );
    const dropoffText = composeAddress(
      trip,
      ['dropoff', 'Destination', 'destination', 'endAddress', 'DestinationAddress'],
      'Destination_AddressLine1', 'Destination_AddressLine2',
      'Destination_City', 'Destination_State', 'Destination_Zip'
    );

    return (
      <div
        key={trip.tripId}
        className="mb-3 overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-100"
      >
        <div className="flex">
          {/* Left accent bar */}
          <div className={`w-1.5 flex-shrink-0 ${isCompleted ? 'bg-gray-300' : 'bg-brand'}`} />

          <div className="min-w-0 flex-1 px-3 py-2.5">
            {/* Trip no + scheduled window */}
            <div className="flex items-start justify-between gap-2">
              <span className="text-sm font-bold text-gray-900">
                Trip #{trip.tripNo ?? '—'}
              </span>
            </div>

            {/* Pickup / dropoff, pill-labelled */}
            <div className="mt-1.5 space-y-1">
              {pickupText && (
                <div className="flex items-center gap-2">
                  <span className="w-16 shrink-0 rounded bg-blue-50 px-1.5 py-0.5 text-center text-[10px] font-semibold text-blue-700">
                    Pickup
                  </span>
                  <span className="min-w-0 flex-1 text-xs text-gray-800">{pickupText}</span>
                </div>
              )}
              {dropoffText && (
                <div className="flex items-center gap-2">
                  <span className="w-16 shrink-0 rounded bg-red-50 px-1.5 py-0.5 text-center text-[10px] font-semibold text-red-600">
                    Dropoff
                  </span>
                  <span className="min-w-0 flex-1 text-xs text-gray-800">{dropoffText}</span>
                </div>
              )}
            </div>

            {/* Passenger, chips, action */}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {trip.passengerName && (
                <span className="text-[11px] text-gray-500">
                  Passenger: <span className="font-bold text-gray-900">{trip.passengerName}</span>
                </span>
              )}

              {duration != null && (
                <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                  {duration} min
                </span>
              )}

              {untilPickup && (
                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600">
                  {untilPickup}
                </span>
              )}

              {isCompleted ? (
                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600">
                  {trip.tripStatus || 'Completed'}
                </span>
              ) : pendingBid ? (
                <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                  Pending approval
                </span>
              ) : trip.assignedToMe ? (
                <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
                  {trip.tripStatus || 'Assigned'}
                </span>
              ) : null}


              {/* Only the actions the server says are allowed. */}
              {!isCompleted && !trip.assignedToMe && (
                <span className="ml-auto flex gap-2">
                  {pendingBid ? (
                    <button
                      onClick={() => handleWithdraw(trip)}
                      disabled={busy || !online}
                      className="rounded-md border border-danger px-4 py-1.5 text-xs font-bold text-danger transition-transform active:scale-95 disabled:opacity-60"
                    >
                      {busy ? '...' : 'Withdraw'}
                    </button>
                  ) : (
                    <>
                      {trip.canClaim && (
                        <button
                          onClick={() => handleClaim(trip)}
                          disabled={busy || !online}
                          className="rounded-md bg-brand px-5 py-1.5 text-xs font-bold text-white transition-transform active:scale-95 disabled:opacity-60"
                        >
                          {busy ? '...' : !online ? 'Offline' : 'Claim'}
                        </button>
                      )}
                      {trip.canBid && (
                        <button
                          onClick={() => handleBid(trip)}
                          disabled={busy || !online}
                          className="rounded-md border border-brand px-4 py-1.5 text-xs font-bold text-brand transition-transform active:scale-95 disabled:opacity-60"
                        >
                          {busy ? '...' : 'Bid'}
                        </button>
                      )}
                    </>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const list =
    scope === 'completed' ? completed : subTab === 'active' ? activeTrips : availableTrips;

  let emptyMessage;
  if (scope === 'completed') emptyMessage = 'You have no completed trips yet.';
  else if (subTab === 'active') emptyMessage = 'You have no pending bids.';
  else emptyMessage = 'Nothing available right now.';

  return (
    <div>
      {/* Scope tabs — Available / Completed */}
      <div className="mb-3 grid grid-cols-2 gap-3">
        {SCOPES.map((s) => {
          const Icon = s.Icon;
          const active = scope === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setScope(s.id)}
              className={`flex items-center justify-center gap-2 rounded-xl border-2 px-2 py-2 text-sm font-semibold transition-colors ${
                active
                  ? 'border-brand bg-blue-50 text-brand'
                  : 'border-transparent bg-white text-gray-400'
              }`}
            >
              <Icon style={{ fontSize: 18 }} />
              {s.label}
            </button>
          );
        })}
      </div>

      {/* AVAILABLE TRIPS / ACTIVE sub-tabs (Available scope only) */}
      {scope === 'available' && (
        <div className="mb-2 grid grid-cols-2 gap-3">
          {[
            { id: 'trips', label: 'Available Trips' },
            { id: 'active', label: 'Active' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setSubTab(t.id)}
              className={`rounded-lg py-2 text-xs font-bold uppercase tracking-wide transition-colors ${
                subTab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'bg-transparent text-gray-400'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {!online && (
        <div className="mb-3 rounded-xl bg-amber-50 px-4 py-3 text-center text-sm font-medium text-amber-800">
          You are offline — connect to claim or bid on a trip.
        </div>
      )}

      {scope === 'available' && online && !unavailable && !vehicleId && (
        <div className="mb-3 rounded-xl bg-amber-50 px-4 py-3 text-center text-sm font-medium text-amber-800">
          Select your vehicle to claim today&apos;s live-dispatch trips.
        </div>
      )}

      {unavailable && (
        <div className="rounded-xl bg-white px-4 py-8 text-center text-sm text-gray-500 shadow-sm">
          The trip marketplace isn&apos;t available for your fleet right now.
        </div>
      )}

      {/* Pool note + day heading */}
      {scope === 'available' && subTab === 'trips' && !unavailable && (
        <div className="mb-1 flex items-center gap-1.5 text-[11px] text-gray-500">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          Unassigned trips longer than {LIVE_DISPATCH_WINDOW_MINUTES} min
        </div>
      )}
      {!unavailable && status === 'loaded' && list.length > 0 && (
        <div className="mb-2 text-sm font-bold text-gray-900">
          {scope === 'completed' ? 'Completed trips' : todayLabel()}
        </div>
      )}

      {status === 'loading' && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-28 animate-pulse rounded-xl bg-gray-200" />
          ))}
        </div>
      )}

      {status === 'error' && (
        <div className="rounded-xl bg-white px-4 py-6 text-center text-sm text-gray-500 shadow-sm">
          {error}
        </div>
      )}

      {status === 'loaded' && !unavailable && !list.length && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <EventBusyOutlined style={{ fontSize: 56 }} className="text-gray-300" />
          <h3 className="mt-3 text-base font-semibold text-gray-800">No trips found</h3>
          <p className="mt-1 max-w-xs text-sm text-gray-500">{emptyMessage}</p>
          <button
            onClick={load}
            className="mt-4 rounded-lg bg-brand px-5 py-2 text-sm font-semibold text-white"
          >
            Refresh
          </button>
        </div>
      )}

      {status === 'loaded' && !unavailable && list.map((trip) => renderCard(trip))}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast || ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </div>
  );
}

export default Marketplace;
